import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { formatWholeUnitPrice } from '../../util/listingDisplay';
import { getListingCapabilities, getRentalDiscounts } from '../../util/dualListing';

import discountBadgeIcon from '../../assets/listing/icon-discount-badge.svg';
import arrowCtaIcon from '../../assets/listing/icon-arrow-cta.svg';

import css from './SectionRentOrBuy.module.css';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';

// Rental discounts apply to weekly (7+ days) and monthly (30+ days) durations.
const WEEKLY_DISCOUNT_DAYS = 7;
const MONTHLY_DISCOUNT_DAYS = 30;

/**
 * One price card: what the path costs, and the action that starts it.
 */
const PriceCard = props => {
  const { label, price, actionLabel, onClick, isDisabled, note, noteId, variant } = props;
  const isBuyCard = variant === 'buyCard';

  return (
    <div className={classNames(css.card, css[variant])}>
      <div className={css.cardText}>
        <span className={css.cardLabel}>{label}</span>
        <span className={classNames(css.cardPrice, { [css.cardPriceSingle]: isBuyCard })}>{price}</span>
      </div>


      <div className={classNames(css.cardAction, { [css.cardActionStacked]: isBuyCard })}>
        {/* <button
          type="button"
          className={classNames(css.actionButton, { [css.actionDisabled]: isDisabled })}
          onClick={onClick}
          disabled={isDisabled}
          aria-describedby={isDisabled && noteId ? noteId : undefined}
        >
          <span className={css.actionLabel}>{actionLabel}</span>
          <span className={css.arrowIcon}>
            <IconCard brand='rightArrow' />
          </span>
        </button> */}
        {note}
      </div>

    </div>
  );
};

/**
 * Rent and Buy prices for a listing, each with its own call to action.
 *
 * Both paths are shown at once rather than behind a choice: a piece that can be
 * rented and bought has two prices, and putting one behind the other makes the
 * cheaper daily rate read as the only price.
 *
 * Renders whichever of the two the listing actually offers, so it covers
 * rent-only, buy-only and both. Returns null when the listing offers neither,
 * or when neither price is available to show.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.publicData listing publicData
 * @param {Object} props.price listing price (Money) — the daily rental rate
 * @param {string} props.currency marketplace currency
 * @param {boolean} [props.isBuyUnavailable] true when there is no stock to sell
 * @param {Function} [props.onRent] handler for the Rent action
 * @param {Function} [props.onBuy] handler for the Buy action
 * @returns {JSX.Element|null}
 */
const SectionRentOrBuy = props => {
  const intl = useIntl();
  const { publicData, price, currency, isBuyUnavailable = false, onRent, onBuy, className } = props;

  const { rent, buy } = getListingCapabilities(publicData);
  const discounts = getRentalDiscounts(publicData);

  const rentalPrice = price ? formatMoney(intl, price) : null;
  const purchasePrice = formatWholeUnitPrice(publicData?.purchase_price, currency, intl);
  const unitType = publicData?.unitType;

  const rentalUnitLabel =
    unitType === 'night'
      ? intl.formatMessage({ id: 'ListingPage.rentPriceUnitNight' })
      : unitType === 'hour'
        ? intl.formatMessage({ id: 'ListingPage.rentPriceUnitHour' })
        : unitType === 'day' || !unitType
          ? intl.formatMessage({ id: 'ListingPage.rentPriceUnitDay' })
          : null;

  const showRent = rent && !!rentalPrice;
  const showBuy = buy && !!purchasePrice;

  if (!showRent && !showBuy) {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      {showRent ? (
        <PriceCard
          variant="rentCard"
          label={<FormattedMessage id="ListingPage.rentalPriceLabel" />}
          price={
            <>
              <span className={css.cardPriceAmount}>{rentalPrice}</span>
              {rentalUnitLabel ? (
                <span className={css.cardPriceUnit}>{rentalUnitLabel}</span>
              ) : null}
            </>
          }
          actionLabel={<FormattedMessage id="ListingPage.rentAction" />}
          onClick={onRent}
        />
      ) : null}

      {showBuy ? (
        <PriceCard
          variant="buyCard"
          label={<FormattedMessage id="ListingPage.buyLabel" />}
          price={<span className={css.cardPriceAmount}>{purchasePrice}</span>}
          actionLabel={<FormattedMessage id="ListingPage.buyAction" />}
          onClick={onBuy}
          isDisabled={isBuyUnavailable}
          noteId="buyUnavailableNote"
          note={
            isBuyUnavailable ? (
              <p className={css.unavailableNote} id="buyUnavailableNote">
                <FormattedMessage id="ListingPage.buyUnavailable" />
              </p>
            ) : null
          }
        />
      ) : null}

      {showRent && (discounts?.weekly || discounts?.monthly) ? (
        <div className={css.discountCard}>
          <span className={css.discountIcon} aria-hidden="true">
            <img src={discountBadgeIcon} alt="" width={32} height={32} />
          </span>
          <div className={css.discountCopy}>
            <p className={css.discountHeadline}>
              <strong>
                <FormattedMessage id="ListingPage.discountHeadline" />
              </strong>{' '}
              {discounts?.weekly ? (
                <FormattedMessage
                  id="ListingPage.longRentalDiscount"
                  values={{ percent: discounts.weekly, days: WEEKLY_DISCOUNT_DAYS }}
                />
              ) : null}
              {discounts?.weekly && discounts?.monthly ? ' ' : null}
              {/* {discounts?.monthly ? (
                <FormattedMessage
                  id="ListingPage.longRentalDiscount"
                  values={{ percent: discounts.monthly, days: MONTHLY_DISCOUNT_DAYS }}
                />
              ) : null} */}
            </p>
            <p className={css.discountNote}>
              <FormattedMessage id="ListingPage.discountAppliedNote" />
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SectionRentOrBuy;
